// Reusable React components shared across GA4 panels.

import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import Pagination from '../../shared/Pagination';
import { SkeletonText } from '../../shared/Skeleton';

/* ----------------------------- sort header -----------------------------
 * The clickable target is a real <button> inside the <th>, not an onClick on the
 * <th> itself. A bare th with a click handler is unreachable by keyboard and
 * announces nothing to a screen reader; `aria-sort` on the th is what conveys
 * the current direction. Matches the accessible pattern already used by
 * competitive-intelligence/RateCalendarPanel.
 */
const SortArrow = ({ active, dir }) => {
  if (!active) return <span className="text-gray-300 dark:text-gray-600 ml-1" aria-hidden="true">↕</span>;
  return <span className="text-[#32B1FF] ml-1" aria-hidden="true">{dir === 'asc' ? '↑' : '↓'}</span>;
};

export const SortableHeader = ({ label, sortKey, currentSort, onSort, className = '' }) => {
  const active = currentSort.key === sortKey;
  return (
    <th
      scope="col"
      className={`px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 ${className}`}
      aria-sort={active ? (currentSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={`Sort by ${label}`}
        className="inline-flex items-center gap-0.5 select-none font-semibold hover:text-gray-800 dark:hover:text-gray-200 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/40 rounded-sm"
      >
        {label}
        <SortArrow active={active} dir={currentSort.dir} />
      </button>
    </th>
  );
};

/* ----------------------------- Pagination Footer -----------------------------
 * Thin adapter over the canonical shared/Pagination. Existing GA4 callers pass
 * `pagination={{ total_items, total_pages }}` — the shared component expects
 * flat `totalItems` / `totalPages` props, so we unpack here. New code should
 * import `Pagination` from '../shared/Pagination' directly.
 */
export const PaginationFooter = ({ pagination, page, perPage, onPageChange, label = 'rows' }) => {
  if (!pagination || (pagination.total_pages ?? 1) <= 1) return null;
  return (
    <Pagination
      page={page}
      totalPages={pagination.total_pages ?? 1}
      onPageChange={onPageChange}
      totalItems={pagination.total_items ?? 0}
      perPage={perPage}
      label={label}
    />
  );
};

/* ----------------------------- Table / Trend view toggle -----------------------------
 * The two-button switch shown above GA4 tables that swaps between the data table
 * and its trend chart. `view` is 'table' | 'trend'; `onChange` receives the next
 * value. Was copy-pasted byte-for-byte into 5 tabs (Sources/Channels/Events/
 * TrafficOverview/Geo) — this is the single source.
 */
export const ViewToggle = ({ view, onChange }) => (
  <div className="flex items-center gap-1 mb-4">
    <button
      type="button"
      onClick={() => onChange('table')}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition ${
        view === 'table' ? 'bg-[#32B1FF] text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
      }`}
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 10h18M3 14h18M3 6h18M3 18h18" /></svg>
      Table View
    </button>
    <button
      type="button"
      onClick={() => onChange('trend')}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition ${
        view === 'trend' ? 'bg-[#32B1FF] text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
      }`}
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 17l6-6 4 4 8-8" /></svg>
      Trend View
    </button>
  </div>
);

/* ----------------------------- Sparkline ----------------------------- */
const getSparklineTrend = (values) => {
  if (!values || values.length < 2) return 'flat';
  const mid = Math.floor(values.length / 2);
  const avgFirst = values.slice(0, mid).reduce((sum, value) => sum + value, 0) / mid;
  const avgSecond = values.slice(mid).reduce((sum, value) => sum + value, 0) / (values.length - mid);
  return avgSecond > avgFirst * 1.05 ? 'up' : avgSecond < avgFirst * 0.95 ? 'down' : 'flat';
};

export const Sparkline = ({ values }) => {
  if (!values || values.length < 2) return <span className="text-gray-400">—</span>;
  const trend = getSparklineTrend(values);
  const color = trend === 'up' ? '#10B981' : trend === 'down' ? '#EF4444' : '#9ca3af';
  const data = values.map((value) => ({ v: value }));
  return (
    <div style={{ width: 80, height: 24 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ----------------------------- Search Bar ----------------------------- */
export const SearchBar = ({ value, onChange, placeholder = 'Search…' }) => {
  const [local, setLocal] = useState(value || '');
  const timer = useRef(null);

  const handleChange = (event) => {
    const nextValue = event.target.value;
    setLocal(nextValue);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(nextValue), 200);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div className="relative max-[589px]:w-full">
      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
      </svg>
      <input
        type="text"
        value={local}
        onChange={handleChange}
        placeholder={placeholder}
        className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#32B1FF] w-56 max-[589px]:w-full"
      />
    </div>
  );
};

/* ----------------------------- Export Menu -----------------------------
 * Lifted to the cross-feature src/components/shared/ExportMenu. Re-exported here
 * so existing GA4 callers (`import { ExportMenu } from "../shared"`) keep working.
 */
export { ExportMenu } from '../../shared/ExportMenu';

// Table-body placeholder used while GA4 tabs (Sources/Events/Channels/Pages/etc.)
// fetch their rows. Roughly matches the visual weight of an 8-row table so the
// page doesn't jump when real data arrives.
export const TableSkeleton = () => (
  <div className="py-4">
    <SkeletonText lines={8} lineClass="h-8" />
  </div>
);

/* ----------------------------- GA4 dimension-mismatch note -----------------------------
 * GA4 sources each metric from a separate aggregate table, so totals for the
 * "same" metric can differ slightly between sections — e.g. the KPI cards
 * (daily traffic table) vs the Events conversion table vs the device/geo
 * tables. This is expected GA4 behaviour, not a data bug. One canonical
 * explanation, dropped in wherever a divergence is visible (Geo, Events
 * conversion breakdown, Tech). A subtle hover/focus tooltip rather than a
 * permanent line of text, so it doesn't add visual noise.
 */
export const GA4_DIMENSION_NOTE =
  "Totals can differ slightly between sections because each metric comes from a separate GA4 table — this is expected in GA4.";

export const GA4DimensionNote = ({ className = '' }) => (
  <span className={`group relative inline-flex items-center align-middle ${className}`}>
    <button
      type="button"
      aria-label={GA4_DIMENSION_NOTE}
      title={GA4_DIMENSION_NOTE}
      className="inline-flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help focus:outline-none focus-visible:ring-1 focus-visible:ring-[#32B1FF] rounded"
    >
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" />
      </svg>
      <span className="italic">Why totals vary</span>
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute left-0 top-full mt-1.5 z-30 w-64 rounded-lg bg-gray-900 dark:bg-gray-700 px-3 py-2 text-[11px] leading-snug text-gray-100 shadow-lg opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
    >
      {GA4_DIMENSION_NOTE}
    </span>
  </span>
);

/* ----------------------------- Section Insight Card ----------------------------- */
export const SectionInsightCard = ({ title, summary, warnings }) => {
  if ((!summary || summary.length === 0) && (!warnings || warnings.length === 0)) return null;
  return (
    <div className="relative bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4 mb-5">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-[#32B1FF] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{title}</h3>
          {summary && summary.length > 0 && (
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{summary.join(' ')}</p>
          )}
          {warnings && warnings.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 pt-3 border-t border-blue-200/50 dark:border-blue-700/30">
              {warnings.map((warning, index) => (
                <span key={index} className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${warning.color === 'red' ? 'bg-red-500' : warning.color === 'amber' ? 'bg-amber-500' : warning.color === 'emerald' ? 'bg-emerald-500' : 'bg-blue-400'}`} />
                  {warning.text}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
